import React from 'react';
import { Box, Image, Text, VStack, HStack, SimpleGrid } from '@chakra-ui/react';
import { ShopifyProduct, ShopifyVariant } from '../../../../types/shopify';
import { calculateProductPricing } from '../../../../utils/shopifyUtils';
import ModalShell from '../../../../components/ui/ModalShell';
import { ff } from '../../../../theme';

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: ShopifyProduct;
  selectedVariant: ShopifyVariant | null;
  productVariants?: string[];
}

export const ProductModal: React.FC<ProductModalProps> = ({
  isOpen,
  onClose,
  product,
  selectedVariant,
  productVariants,
}) => {
  const availableVariants = productVariants 
    ? product.variants.filter(variant => productVariants.includes(variant.id))
    : product.variants;

  const { maxPrice } = calculateProductPricing(product, selectedVariant, productVariants);

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title={product.title} size="3xl" scrollBehavior="inside">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        <Box>
          {product.images[0] && (
            <Image
              src={product.images[0].url}
              alt={product.images[0].altText || product.title}
              objectFit="cover"
              width="100%"
              borderRadius="20px"
            />
          )}
        </Box>
        <VStack align="stretch" spacing={4}>
          <Text fontSize="15px" color={ff.body} lineHeight="1.5">{product.description}</Text>
          <Box borderRadius="20px" bg={ff.limeSurface} px={4} py={3}>
            <Text fontSize="15px" fontWeight="600" color={ff.ink}>
              Product value: ${selectedVariant ? selectedVariant.price : maxPrice}
            </Text>
          </Box>
          {selectedVariant && !(selectedVariant.selectedOptions.length === 1 &&
                 selectedVariant.selectedOptions[0].name === "Title" &&
                 selectedVariant.selectedOptions[0].value === "Default Title") && (
            <Box>
              <Text textStyle="eyebrow" color={ff.text2} mb="10px">Selected options</Text>
              <Box border="1px solid" borderColor={ff.line} borderRadius="20px" overflow="hidden">
                {selectedVariant.selectedOptions.map((option, index) => (
                  <HStack key={index} px={4} minH="48px" justify="space-between" borderTopWidth={index ? '1px' : 0} borderColor={ff.line}>
                    <Text fontSize="14px" color={ff.text2}>{option.name}</Text>
                    <Text fontSize="14px" fontWeight="600" color={ff.ink}>{option.value}</Text>
                  </HStack>
                ))}
              </Box>
            </Box>
          )}
          {availableVariants.length > 1 && !selectedVariant && (
            <Box>
              <Text textStyle="eyebrow" color={ff.text2} mb="10px">Available options</Text>
              <Box border="1px solid" borderColor={ff.line} borderRadius="20px" overflow="hidden">
                {availableVariants.map((variant, index) => (
                  <HStack key={index} px={4} py={3} minH="48px" justify="space-between" borderTopWidth={index ? '1px' : 0} borderColor={ff.line}>
                    <Text fontSize="14px" color={ff.body}>
                      {variant.selectedOptions
                        .map(opt => `${opt.name}: ${opt.value}`)
                        .join(', ')}
                    </Text>
                    <Text fontSize="14px" fontWeight="600" color={ff.text2}>{variant.price}</Text>
                  </HStack>
                ))}
              </Box>
            </Box>
          )}
        </VStack>
      </SimpleGrid>
    </ModalShell>
  );
}; 